import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Trophy, Flame, Image as ImageIcon, Star, Crown, TrendingUp, X } from "lucide-react";
import api from "@/lib/api";
import { ASSETS } from "@/lib/assets";
import { renderRankCard } from "@/lib/rankCard";
import { withXHandle } from "@/lib/memeKit";
import { useSettings } from "@/hooks/useSettings";
import ShareToXDialog from "@/components/ShareToXDialog";
import BadgeTag from "@/components/BadgeTag";
import Confetti from "@/components/Confetti";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = [
  { key: "rep", label: "Top Robbers", Icon: Crown, metric: "rep", suffix: "REP" },
  { key: "votes", label: "Most Votes", Icon: Flame, metric: "votesReceived", suffix: "votes" },
  { key: "wins", label: "Most Wins", Icon: Trophy, metric: "wins", suffix: "wins" },
  { key: "memes", label: "Most Memes", Icon: ImageIcon, metric: "memes", suffix: "memes" },
  { key: "favorites", label: "Community Faves", Icon: Star, metric: "favorites", suffix: "faves" },
];
const PERIODS = [
  { key: "all", label: "All-Time" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
];

// gold / silver / bronze meta for the podium
const PODIUM = [
  { medal: "🥇", ring: "border-gold", text: "text-gold", bar: "bg-gold", h: "h-28 sm:h-36", order: "order-2" },
  { medal: "🥈", ring: "border-cream", text: "text-cream", bar: "bg-cream", h: "h-20 sm:h-28", order: "order-1" },
  { medal: "🥉", ring: "border-[#cd7f32]", text: "text-[#cd7f32]", bar: "bg-[#cd7f32]", h: "h-16 sm:h-24", order: "order-3" },
];

function Avatar({ r, size = "w-14 h-14 sm:w-16 sm:h-16", ring = "border-neon" }) {
  return r.picture
    ? <img src={r.picture} alt="" className={`${size} rounded-full border-3 ${ring} object-cover`} />
    : <span className={`${size} rounded-full bg-neon text-black font-display font-900 flex items-center justify-center text-xl border-3 ${ring}`}>{(r.displayName || "R").charAt(0).toUpperCase()}</span>;
}



export default function Leaderboard() {
  const { user } = useAuth();
  const { data: settings } = useSettings();
  const [category, setCategory] = useState("rep");
  const [period, setPeriod] = useState("all");
  const cat = CATEGORIES.find((c) => c.key === category);

  const { data, isLoading } = useQuery({
    queryKey: ["leaderboard", category, period],
    queryFn: async () => (await api.get(`/leaderboard?category=${category}&period=${period}`)).data,
  });
  const rows = data?.rows || [];
  const me = data?.me;
  const top3 = rows.slice(0, 3);
  const rest = rows.slice(3);

  // Rank-up celebration — compare against the last rank we saw for this board.
  const celebrated = useRef(false);
  const [rankUp, setRankUp] = useState(null); // { from, to } when an improvement happens
  useEffect(() => {
    if (!user || !me?.rank) return;
    const key = `robbed_rank_${user.user_id}_${category}_${period}`;
    const prev = parseInt(localStorage.getItem(key) || "0", 10);
    if (prev && me.rank < prev && !celebrated.current) {
      celebrated.current = true;
      setRankUp({ from: prev, to: me.rank });
      api.post("/analytics/event", { name: "rank_up_celebration_shown", meta: { category, period, from: prev, to: me.rank } }).catch(() => {});
    }
    localStorage.setItem(key, String(me.rank));
  }, [me?.rank, user, category, period]);
  // reset the celebration guard when switching boards
  useEffect(() => { celebrated.current = false; }, [category, period]);

  // Leaderboard Share — polished achievement card via the shared ShareToXDialog + honest image sharing.
  const rankCanvasRef = useRef(null);
  const [rankShareOpen, setRankShareOpen] = useState(false);
  const [rankPreview, setRankPreview] = useState(null);
  const [rankCanNative, setRankCanNative] = useState(false);
  const rankBlobRef = useRef(null);
  const rankFileRef = useRef(null);
  const rankTextRef = useRef("");
  const rankShareSourceRef = useRef("leaderboard"); // 'leaderboard' | 'rankup' — drives analytics event name
  const openRankShare = async (source = "leaderboard") => {
    if (!me) return;
    rankShareSourceRef.current = source;
    renderRankCard(rankCanvasRef.current, {
      rank: me.rank, total: me.total, name: me.displayName, value: me.value,
      suffix: cat.suffix, catLabel: cat.label, level: me.level, levelName: me.levelName,
    });
    rankTextRef.current = withXHandle(
      `I'm ranked #${me.rank} of ${me.total} on the $ROBBED ${cat.label} leaderboard — ${me.value} ${cat.suffix}, ROB LVL ${me.level} ${me.levelName}. THEY GOT RICH. WE GOT ROBBED.`,
      settings?.xUrl);
    const canvas = rankCanvasRef.current;
    let preview = null; try { preview = canvas.toDataURL("image/png"); } catch { /* noop */ }
    setRankPreview(preview);
    let blob = null; try { blob = await new Promise((r) => canvas.toBlob(r, "image/png")); } catch { /* noop */ }
    rankBlobRef.current = blob;
    const file = blob ? new File([blob], "robbed-rank.png", { type: "image/png" }) : null;
    rankFileRef.current = file;
    setRankCanNative(!!(file && navigator.canShare && navigator.canShare({ files: [file] })));
    setRankUp(null); // close the celebration if this share came from it
    setRankShareOpen(true);
  };
  const rankShareEvent = () => (rankShareSourceRef.current === "rankup" ? "rank_up_share_x" : "leaderboard_share_x");
  const doNativeRankShare = async () => {
    try {
      await navigator.share({ files: [rankFileRef.current], text: rankTextRef.current });
      api.post("/analytics/event", { name: rankShareEvent(), meta: { method: "native", image: "attached", category, rank: me?.rank, user_id: me?.user_id } }).catch(() => {});
      setRankShareOpen(false);
    } catch (err) {
      if (err?.name === "AbortError") return; // cancelled native sheet
    }
  };
  const onManualRankInitiated = () => {
    api.post("/analytics/event", { name: rankShareEvent(), meta: { image: "manual", category, rank: me?.rank, user_id: me?.user_id } }).catch(() => {});
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
      {/* HERO */}
      <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-8 lg:gap-6 items-center">
        <div>
          <span className="tag">The Crew Rankings</span>
          <h1 className="headline text-cream text-4xl sm:text-6xl mt-5">LEADER<span className="text-neon">BOARD</span></h1>
          <p className="font-mono text-dim mt-4 max-w-xl">
            Real community activity, ranked. Earn ROBBED REP, collect badges, and climb. No financial rewards — just the crown and bragging rights.
          </p>
          <div className="hidden lg:flex gap-4 mt-6">
            <div className="border-l-3 border-neon pl-3">
              <p className="font-display font-900 text-neon text-xl">10</p>
              <p className="font-mono text-[10px] uppercase text-dim">ROB Levels</p>
            </div>
            <div className="border-l-3 border-gold pl-3">
              <p className="font-display font-900 text-gold text-xl">8</p>
              <p className="font-mono text-[10px] uppercase text-dim">Badges</p>
            </div>
            <div className="border-l-3 border-grape pl-3">
              <p className="font-display font-900 text-grape text-xl">3</p>
              <p className="font-mono text-[10px] uppercase text-dim">Time Periods</p>
            </div>
          </div>
        </div>
        <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
          className="order-first lg:order-last flex justify-center">
          <img
            src={ASSETS.leaderboardHero}
            alt="ROBBED champion holding a trophy on the #1 podium beside the leaderboard"
            className="w-auto max-h-64 sm:max-h-80 lg:max-h-[380px] object-contain drop-shadow-[0_0_25px_rgba(57,255,20,0.25)]"
            data-testid="leaderboard-hero-mascot"
          />
        </motion.div>
      </div>

      {/* Period tabs */}
      <div className="flex gap-2 mt-6 flex-wrap" data-testid="lb-periods">
        {PERIODS.map((p) => (
          <button key={p.key} onClick={() => setPeriod(p.key)} data-testid={`lb-period-${p.key}`}
            className={`px-4 py-2 font-heading font-bold uppercase text-xs border-3 border-black transition-colors ${period === p.key ? "bg-neon text-black" : "bg-ink2 text-dim hover:text-cream"}`}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Category selector */}
      <div className="flex gap-2 mt-3 flex-wrap" data-testid="lb-categories">
        {CATEGORIES.map((c) => (
          <button key={c.key} onClick={() => setCategory(c.key)} data-testid={`lb-cat-${c.key}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 font-mono text-[11px] uppercase border-2 transition-colors ${category === c.key ? "border-neon text-neon bg-neon/10" : "border-charcoal text-dim hover:text-cream"}`}>
            <c.Icon className="w-3.5 h-3.5" /> {c.label}
          </button>
        ))}
      </div>

      {/* Your rank strip (logged-in members) */}
      {user && !isLoading && (
        me ? (
          <div className="comic-card mt-5 p-3 sm:p-4 flex items-center gap-3 !bg-ink border-neon" data-testid="my-rank-strip">
            <span className="font-display font-900 text-neon text-2xl sm:text-3xl leading-none shrink-0">#{me.rank}</span>
            <div className="flex-1 min-w-0">
              <p className="font-heading font-bold uppercase text-cream text-sm">Your Rank <span className="text-dim">· of {me.total}</span></p>
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] text-dim truncate">{cat.label}</span>
                <BadgeTag rob={{ level: me.level, name: me.levelName }} />
              </div>
            </div>
            <div className="text-right shrink-0 hidden sm:block">
              <p className="font-display font-900 text-neon text-lg leading-none">{me.value}</p>
              <p className="font-mono text-[9px] uppercase text-dim">{cat.suffix}</p>
            </div>
            <button onClick={() => openRankShare("leaderboard")} className="btn-neon !py-2 !px-3 !text-[11px] shrink-0" data-testid="share-rank-x-btn">
              <Trophy className="w-3.5 h-3.5" />
              Share My #{me.rank}
            </button>
          </div>
        ) : (
          <div className="comic-card mt-5 p-3 sm:p-4 flex items-center justify-between gap-3 !bg-ink" data-testid="my-rank-strip">
            <p className="font-mono text-xs text-dim">You're not on the <span className="text-cream">{cat.label}</span> board yet for this period.</p>
            <Link to="/memes" className="btn-neon !py-1.5 !px-3 !text-[11px] shrink-0">Climb In</Link>
          </div>
        )
      )}

      {isLoading ? (
        <p className="font-mono text-dim mt-10">Counting the damage...</p>
      ) : rows.length === 0 ? (
        <div className="comic-card mt-8 flex flex-col items-center py-14 text-center">
          <span className="text-5xl">🏆</span>
          <p className="font-display text-cream mt-3 uppercase text-lg">No {cat.label} yet for this period.</p>
          <p className="font-mono text-dim text-sm mt-1">Be the first to climb. Make a meme, enter a battle, get crowned.</p>
          <Link to="/memes" className="btn-neon mt-6">Make A Meme</Link>
        </div>
      ) : (
        <>
          {/* PODIUM — top 3 */}
          <div className="flex items-end justify-center gap-2 sm:gap-4 mt-10" data-testid="lb-podium">
            {top3.map((r, i) => {
              const p = PODIUM[i];
              return (
                <motion.div key={`${r.user_id}-${category}-${period}`} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                  className={`relative flex-1 max-w-[180px] flex flex-col items-center ${p.order}`} data-testid={`lb-podium-${i}`}>
                  {i === 0 && <Confetti />}
                  {i === 0 ? (
                    <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                      className="relative z-10 mb-1" data-testid="podium-crown">
                      <Crown className="w-9 h-9 sm:w-11 sm:h-11 text-gold drop-shadow-[0_0_12px_rgba(255,215,0,0.75)]" fill="currentColor" strokeWidth={1.5} />
                    </motion.div>
                  ) : (
                    <span className="text-2xl sm:text-3xl mb-1">{p.medal}</span>
                  )}
                  <Link to={`/u/${r.user_id}`} className="flex flex-col items-center group">
                    <Avatar r={r} ring={p.ring} />
                    <span className="font-display font-900 text-cream text-xs sm:text-sm mt-2 text-center truncate max-w-[140px] group-hover:text-neon">{r.displayName}</span>
                  </Link>
                  <div className="flex items-center gap-1 mt-1">
                    <BadgeTag rob={{ level: r.level, name: r.levelName }} />
                  </div>
                  <p className={`font-display font-900 ${p.text} text-lg sm:text-xl mt-1`}>{r[cat.metric]}</p>
                  <p className="font-mono text-[9px] uppercase text-dim">{cat.suffix}</p>
                  <div className={`w-full ${p.h} ${p.bar} border-3 border-black mt-2 flex items-start justify-center`}>
                    <span className="font-display font-900 text-black text-2xl sm:text-3xl mt-1">{i + 1}</span>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Ranked list — ranks 4+ */}
          {rest.length > 0 && (
            <div className="comic-card mt-6 divide-y-3 divide-black overflow-hidden" data-testid="lb-rows">
              {rest.map((r, i) => {
                const rank = i + 4;
                return (
                  <motion.div key={r.user_id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.02 }}
                    className={`flex items-center gap-3 p-3 sm:p-4 hover:bg-ink2 transition-colors ${me && r.user_id === me.user_id ? "!bg-neon/10" : ""}`} data-testid={`lb-row-${rank}`}>
                    <span className="font-display font-900 text-xl w-8 text-center text-dim">{rank}</span>
                    <Avatar r={r} size="w-10 h-10" />
                    <div className="flex-1 min-w-0">
                      <Link to={`/u/${r.user_id}`} className="font-display font-900 text-cream text-sm sm:text-base hover:text-neon truncate block">
                        {r.displayName}
                      </Link>
                      <div className="flex items-center gap-2">
                        {r.username && <span className="font-mono text-[10px] text-neon truncate">@{r.username}</span>}
                        <BadgeTag rob={{ level: r.level, name: r.levelName }} />
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-display font-900 text-neon text-lg leading-none">{r[cat.metric]}</p>
                      <p className="font-mono text-[9px] uppercase text-dim">{cat.suffix}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </>
      )}

      <canvas ref={rankCanvasRef} className="hidden" aria-hidden="true" />

      {rankUp && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          data-testid="rank-up-celebration" onClick={() => setRankUp(null)}>
          <Confetti />
          <motion.div initial={{ scale: 0.85, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="comic-card !bg-ink border-neon max-w-sm w-full p-6 text-center relative">
            <button onClick={() => setRankUp(null)} data-testid="rank-up-dismiss"
              className="absolute top-3 right-3 text-dim hover:text-cream transition-colors"><X className="w-5 h-5" /></button>
            <TrendingUp className="w-10 h-10 text-neon mx-auto drop-shadow-[0_0_12px_rgba(57,255,20,0.6)]" />
            <h3 className="font-display font-900 uppercase text-cream text-2xl mt-3">You Moved Up!</h3>
            <p className="font-mono text-[11px] uppercase text-dim mt-1">{cat.label} · {PERIODS.find((p) => p.key === period)?.label}</p>
            <div className="flex items-center justify-center gap-3 my-5" data-testid="rank-up-transition">
              <span className="font-display font-900 text-dim text-3xl line-through decoration-2">#{rankUp.from}</span>
              <TrendingUp className="w-6 h-6 text-neon" />
              <span className="font-display font-900 text-neon text-5xl drop-shadow-[0_0_10px_rgba(57,255,20,0.5)]">#{rankUp.to}</span>
            </div>
            <button onClick={() => openRankShare("rankup")} className="btn-neon w-full !text-sm" data-testid="rank-up-share-btn">
              <Trophy className="w-4 h-4" /> Share Your New Rank on X
            </button>
            <button onClick={() => setRankUp(null)} className="btn-ghost w-full !py-2 !text-[11px] mt-2" data-testid="rank-up-later-btn">Maybe Later</button>
          </motion.div>
        </div>
      )}

      <ShareToXDialog
        open={rankShareOpen} onOpenChange={setRankShareOpen}
        preview={rankPreview} canNative={rankCanNative}
        blob={rankBlobRef.current} filename="robbed-rank.png" text={rankTextRef.current}
        onNativeShare={doNativeRankShare} onManualInitiated={onManualRankInitiated}
        testidPrefix="rank-share" title="Share My Rank on X"
      />
    </div>
  );
}
